import { deviceLocal } from '@/privacy/deviceStorage';
import { dropEverythingOfThisPerson } from './ownerRule';
import { noteDataRemovedUnchecked } from './signOutDataNotice';
import { readCachedSignOutRequired } from './signOutRequirementCache';

/**
 * I-2092 (under I-1895): signing out can also remove the person's data from the device. The owner rule
 * keeps a person's copy across their own re-sign-in, and stays the default; this is the person's
 * choice to remove it, and an organisation's to require it.
 *
 * The choice is remembered per device, in this one key (`device` in `STORE_REGISTER`: it is about the
 * browser, not about who signed in, so the owner rule leaves it for the next person to see as it was).
 */
export const SIGN_OUT_REMOVES_DATA_KEY = 'featit:signOutRemovesData';

/** What the person chose last on this device; off until they tick it. */
export function readSignOutRemovesData(): boolean {
  try {
    return deviceLocal.getItem(SIGN_OUT_REMOVES_DATA_KEY) === '1';
  } catch {
    return false;
  }
}

/** Remembers the choice on this device; off removes the key, so the default leaves nothing behind. */
export function rememberSignOutRemovesData(on: boolean): void {
  try {
    if (on) deviceLocal.setItem(SIGN_OUT_REMOVES_DATA_KEY, '1');
    else deviceLocal.removeItem(SIGN_OUT_REMOVES_DATA_KEY);
  } catch {
    // A device that will not remember it asks again next time.
  }
}

/**
 * Whether the person's organisations require the removal: `required` names them; `checking` and
 * `unread` are the two ways the answer is not here yet, and the dialog treats neither as "no".
 */
export type SignOutRequirementType =
  | { state: 'optional' }
  | { state: 'required'; organisations: string[] }
  | { state: 'checking' }
  | { state: 'unread' };

/** The namespace store's state that answers it: the tree the person already loads. */
export function signOutRequirement(store: {
  tree: ReadonlyArray<{
    kind: string;
    parentId: string | null;
    name: string;
    path: string;
    signOutRemovesDeviceData?: boolean;
  }> | null;
  loading: boolean;
}): SignOutRequirementType {
  if (store.tree === null) return { state: store.loading ? 'checking' : 'unread' };
  const organisations = store.tree
    .filter(
      (namespace) =>
        namespace.kind === 'group' &&
        namespace.parentId === null &&
        namespace.signOutRemovesDeviceData === true,
    )
    .map((namespace) => namespace.name || namespace.path);
  return organisations.length > 0 ? { state: 'required', organisations } : { state: 'optional' };
}

/**
 * Signs out, removing the person's data from this device first when asked: before the session ends,
 * while the code that knows which stores are theirs is still running as them. A removal that fails
 * never keeps the person signed in.
 */
export function signOutOfThisDevice(options: { removeData: boolean; logout: () => void }): void {
  if (options.removeData) {
    try {
      dropEverythingOfThisPerson();
    } catch (error) {
      console.warn('[featit] sign-out: the data on this device was not removed', error);
    }
  }
  options.logout();
}

/**
 * I-2102: what a sign-out that asked nobody has to go on. The namespace tree answers when it is here;
 * otherwise the last answer kept on the device (`signOutRequirementCache`); with neither it is
 * `unanswered`, and the sign-out removes the data (it fails closed, as the dialog does: a copy
 * removed loses nothing FEATit does not hold).
 */
export type SignOutAnswerType = 'required' | 'optional' | 'unanswered';

export function signOutAnswerWithoutAsking(
  store: Parameters<typeof signOutRequirement>[0],
): SignOutAnswerType {
  const known = signOutRequirement(store);
  if (known.state === 'required') return 'required';
  if (known.state === 'optional') return 'optional';
  const kept = readCachedSignOutRequired();
  return kept === null ? 'unanswered' : kept ? 'required' : 'optional';
}

/**
 * Registered with `AuthStore.onBeforeSignOut`, so every `logout()` honours the requirement, whichever
 * screen called it, while the code that knows which stores are the person's is still running as them.
 * With no answer at all the data is removed and the sign-in page says why (`signOutDataNotice`).
 */
export function removeDataWhereRequired(store: Parameters<typeof signOutRequirement>[0]): void {
  const answer = signOutAnswerWithoutAsking(store);
  if (answer === 'optional') return;
  try {
    dropEverythingOfThisPerson();
  } catch (error) {
    console.warn('[featit] sign-out: the data on this device was not removed', error);
  }
  if (answer === 'unanswered') noteDataRemovedUnchecked();
}
